<script setup lang="ts">
const downloadButtons = [
  { name: "App Store", icon: "material-symbols:apple", color: "bg-dark-gray" },
  { name: "Google Play", icon: "mdi:google-play", color: "bg-primary" },
];
</script>

<template>
  <div class="relative">
    <!-- Main Phone Mockup -->
    <div
      class="rounded-[2rem] sm:rounded-[2.5rem] 3xl:rounded-[3rem] overflow-hidden shadow-warm-lg bg-white border-4 sm:border-8 border-dark-gray/90 max-w-[220px] sm:max-w-[280px] 2xl:max-w-[320px] 3xl:max-w-[380px] 4xl:max-w-[440px] mx-auto"
    >
      <!-- Phone Notch -->
      <div
        class="absolute top-0 left-1/2 -translate-x-1/2 w-16 sm:w-24 2xl:w-28 3xl:w-32 h-4 sm:h-6 2xl:h-7 3xl:h-8 bg-dark-gray/90 rounded-b-xl sm:rounded-b-2xl z-10"
      ></div>

      <!-- Screen Content -->
      <div
        class="bg-gradient-to-b from-cream to-white pt-7 sm:pt-10 2xl:pt-12 3xl:pt-14 px-3 sm:px-5 2xl:px-6 3xl:px-8 pb-4 sm:pb-6 2xl:pb-8 3xl:pb-10"
      >
        <!-- App Logo -->
        <div class="flex flex-col items-center mb-4 sm:mb-6 2xl:mb-8 3xl:mb-10">
          <img
            src="/hirable-icon.png"
            alt="Hirable"
            class="w-12 h-12 sm:w-16 sm:h-16 2xl:w-20 2xl:h-20 3xl:w-24 3xl:h-24 4xl:w-28 4xl:h-28 mb-2 sm:mb-3 2xl:mb-4"
          />
          <h3 class="text-sm sm:text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl font-secondary font-bold text-dark-gray">
            {{ $config.public.appName }}
          </h3>
          <p class="text-[10px] sm:text-xs 2xl:text-sm 3xl:text-base text-medium-gray">Your Career Companion</p>
        </div>

        <!-- Sign Up Form -->
        <div class="space-y-2 sm:space-y-3 2xl:space-y-4 3xl:space-y-5 pointer-events-none">
          <div>
            <div
              class="h-8 sm:h-10 2xl:h-12 3xl:h-14 bg-cream rounded-lg sm:rounded-xl 2xl:rounded-2xl flex items-center px-2 sm:px-3 2xl:px-4 border border-sand"
            >
              <Icon
                name="material-symbols:mail"
                class="w-3 h-3 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-medium-gray mr-1.5 sm:mr-2 2xl:mr-3"
              />
              <span class="text-[10px] sm:text-sm 2xl:text-base 3xl:text-lg text-medium-gray">Email address</span>
            </div>
          </div>
          <div>
            <div
              class="h-8 sm:h-10 2xl:h-12 3xl:h-14 bg-cream rounded-lg sm:rounded-xl 2xl:rounded-2xl flex items-center px-2 sm:px-3 2xl:px-4 border border-sand"
            >
              <Icon
                name="material-symbols:lock"
                class="w-3 h-3 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-medium-gray mr-1.5 sm:mr-2 2xl:mr-3"
              />
              <span class="text-[10px] sm:text-sm 2xl:text-base 3xl:text-lg text-medium-gray">Create password</span>
            </div>
          </div>
          <div
            class="w-full h-8 sm:h-10 2xl:h-12 3xl:h-14 rounded-lg sm:rounded-xl 2xl:rounded-2xl bg-gradient-to-r from-primary to-primary-light text-white font-semibold text-[10px] sm:text-sm 2xl:text-base 3xl:text-lg shadow-sunset flex items-center justify-center"
          >
            Create Free Account
          </div>
        </div>

        <!-- Divider -->
        <div class="flex items-center gap-2 sm:gap-3 my-3 sm:my-4 2xl:my-5 3xl:my-6">
          <div class="flex-1 h-px bg-sand"></div>
          <span class="text-[10px] sm:text-xs 2xl:text-sm 3xl:text-base text-medium-gray whitespace-nowrap">or continue with</span>
          <div class="flex-1 h-px bg-sand"></div>
        </div>

        <!-- Social Login -->
        <div class="flex gap-2 sm:gap-3 2xl:gap-4 pointer-events-none">
          <div
            class="flex-1 h-8 sm:h-10 2xl:h-12 3xl:h-14 rounded-lg sm:rounded-xl 2xl:rounded-2xl bg-white border border-sand flex items-center justify-center"
          >
            <Icon name="logos:google-icon" class="w-4 h-4 sm:w-5 sm:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7" />
          </div>
          <div
            class="flex-1 h-8 sm:h-10 2xl:h-12 3xl:h-14 rounded-lg sm:rounded-xl 2xl:rounded-2xl bg-dark-gray flex items-center justify-center"
          >
            <Icon name="mdi:apple" class="w-4 h-4 sm:w-5 sm:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-white" />
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Time Badge -->
    <div
      class="hidden lg:flex absolute -left-4 lg:-left-8 2xl:-left-12 bottom-1/3 z-20 bg-gradient-to-r from-gold to-gold-light rounded-full shadow-gold px-4 py-2 2xl:px-6 2xl:py-3 3xl:px-8 3xl:py-4 4xl:px-10 4xl:py-5 animate-gentle-float"
      style="animation-delay: 0.6s"
    >
      <div class="flex items-center gap-2 2xl:gap-3">
        <Icon name="material-symbols:timer" class="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
        <span class="text-sm 2xl:text-base 3xl:text-lg font-semibold text-white">2 min setup</span>
      </div>
    </div>

    <!-- Decorative blur -->
    <div class="absolute -z-10 -top-8 -right-8 w-48 h-48 2xl:w-64 2xl:h-64 3xl:w-80 3xl:h-80 bg-primary/10 rounded-full blur-3xl"></div>
  </div>
</template>
